import { QueryClient, type DefaultOptions } from '@tanstack/react-query'

// Default query options
const defaultOptions: DefaultOptions = {
  queries: {
    // Data is considered fresh for 5 minutes
    staleTime: 5 * 60 * 1000,
    
    // Keep data in cache for 30 minutes after component unmount
    gcTime: 30 * 60 * 1000,
    
    // Retry failed requests up to 3 times
    retry: 3,
    
    // Increase delay between retries
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    
    // Refetch on window focus (useful for keeping data fresh)
    refetchOnWindowFocus: true,
    
    // Don't refetch on mount if data is not stale
    refetchOnMount: true,
    
    // Refetch on reconnect
    refetchOnReconnect: true,
  },
  mutations: {
    // Retry mutations only once
    retry: 1,
    
    // Retry delay for mutations
    retryDelay: 1000,
  },
}

// Create the query client instance
export const queryClient = new QueryClient({
  defaultOptions,
})

// Query key factory for consistent key generation
export const queryKeys = {
  // Auth
  auth: {
    all: ['auth'] as const,
    user: () => [...queryKeys.auth.all, 'user'] as const,
    permissions: () => [...queryKeys.auth.all, 'permissions'] as const,
  },
  
  // Assistants
  assistants: {
    all: ['assistants'] as const,
    lists: () => [...queryKeys.assistants.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.assistants.lists(), filters] as const,
    details: () => [...queryKeys.assistants.all, 'detail'] as const,
    detail: (id: string | number) =>
      [...queryKeys.assistants.details(), id] as const,
  },
  
  // Conversations
  conversations: {
    all: ['conversations'] as const,
    lists: () => [...queryKeys.conversations.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.conversations.lists(), filters] as const,
    details: () => [...queryKeys.conversations.all, 'detail'] as const,
    detail: (id: string | number) =>
      [...queryKeys.conversations.details(), id] as const,
    transcript: (id: string | number) =>
      [...queryKeys.conversations.detail(id), 'transcript'] as const,
  },
  
  // Customers
  customers: {
    all: ['customers'] as const,
    lists: () => [...queryKeys.customers.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.customers.lists(), filters] as const,
    details: () => [...queryKeys.customers.all, 'detail'] as const,
    detail: (id: string | number) =>
      [...queryKeys.customers.details(), id] as const,
  },
  
  // Users
  users: {
    all: ['users'] as const,
    lists: () => [...queryKeys.users.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.users.lists(), filters] as const,
    details: () => [...queryKeys.users.all, 'detail'] as const,
    detail: (id: string | number) => [...queryKeys.users.details(), id] as const,
  },
  
  // Tasks
  tasks: {
    all: ['tasks'] as const,
    lists: () => [...queryKeys.tasks.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.tasks.lists(), filters] as const,
    details: () => [...queryKeys.tasks.all, 'detail'] as const,
    detail: (id: string | number) => [...queryKeys.tasks.details(), id] as const,
  },
  
  // Dashboard
  dashboard: {
    all: ['dashboard'] as const,
    stats: (filters?: Record<string, unknown>) =>
      [...queryKeys.dashboard.all, 'stats', filters] as const,
  },
  
  // Partners
  partners: {
    all: ['partners'] as const,
    lists: () => [...queryKeys.partners.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.partners.lists(), filters] as const,
    details: () => [...queryKeys.partners.all, 'detail'] as const,
    detail: (id: string | number) =>
      [...queryKeys.partners.details(), id] as const,
  },
} as const

export default queryClient
